import React, { useState, useEffect } from "react";
import { DataToolbar } from "../components/DataToolbar";
import { Modal } from "../components/Modal";
import { useToast } from "../components/Toast";
import { db, serverTimestamp } from "../../firebaseClient";
import { useAuth } from "../context/AuthContext";
import { ApprovalFlowPanel } from "../components/approval/ApprovalFlowPanel";
import { createOrResetApprovalFlow, updateStageDecision } from "../services/approvalFlowService";
import { useApprovalFlow } from "../hooks/useApprovalFlow";
import { DEPARTMENTS, getDeptNameByCode } from "../constants/departments";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { uploadUnifiedFiles } from "../services/unifiedAttachmentService";
import { isLegal, isGovAdmin, canManageAllRequests, getDeptCodeByEmail } from "../config/roles";
import type { ContractTemplate } from "../types/legalOps";
import { useAiContext } from "../ai/AiContext";
import { generateDocCode } from "../utils/idGenerator";
import { CONTRACT_TYPE_OPTIONS } from "../constants/contractTypes";
import { AiFieldHeader } from "../components/shared/AiFieldHeader";
import { analyzeUploadedFile } from "../services/aiService";
import type { AttachmentMeta } from "../types/attachments";

export default function TemplatesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<ContractTemplate[]>([]);
  const [filteredItems, setFilteredItems] = useState<ContractTemplate[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [contractTypeFilter, setContractTypeFilter] = useState("ALL");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ContractTemplate | null>(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [flowItem, setFlowItem] = useState<ContractTemplate | null>(null);
  const [isFlowModalOpen, setIsFlowModalOpen] = useState(false);

  const canManage = isLegal(user?.email) || isGovAdmin(user?.email) || canManageAllRequests(user?.email);
  const userDeptCode = getDeptCodeByEmail(user?.email);
  const userDeptName = getDeptNameByCode(userDeptCode);

  const { requestDelete } = useAiContext();
  const { showToast } = useToast();

  useEffect(() => {
    const unsub = db
      .collection("contract_templates")
      .orderBy("createdAt", "desc")
      .onSnapshot(
        (snap) => {
          const list = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as ContractTemplate[];
          setItems(list);
        },
        (err) => {
          console.error(err);
          showToast("載入失敗", "error");
        }
      );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let result = items;

    if (!canManage) {
      result = result.filter((item) => {
        if (item.status !== "APPROVED" && item.status !== "PUBLISHED") return false;

        const visible = item.departments || [];
        if (visible.includes("all") || visible.includes("ALL")) return true;

        return visible.includes(userDeptCode) || visible.includes(userDeptName);
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (i) =>
          (i.title || "").toLowerCase().includes(term) ||
          (i.description || "").toLowerCase().includes(term) ||
          (i.docCode || "").toLowerCase().includes(term)
      );
    }

    if (contractTypeFilter !== "ALL") {
      result = result.filter((i) => i.contractType === contractTypeFilter);
    }

    setFilteredItems(result);
  }, [items, searchTerm, contractTypeFilter, canManage, userDeptCode, userDeptName]);

  const handleCreate = () => {
    setViewOnly(false);
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: ContractTemplate) => {
    setViewOnly(false);
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleView = (item: ContractTemplate) => {
    setViewOnly(true);
    setEditingItem(item);
    setIsModalOpen(true);
  };


  const handleSaved = () => {
    setIsModalOpen(false);
    setViewOnly(false);
    setEditingItem(null);
    showToast("已儲存");
  };


  const handleFlow = (item: ContractTemplate) => {
    setFlowItem(item);
    setIsFlowModalOpen(true);
  };

  const handleDelete = (item: ContractTemplate) => {
    requestDelete({
      targetType: "templateContract",
      targetId: item.id,
      summary: `範本：${item.title}`,
      onConfirm: async () => {
        await db.collection("contract_templates").doc(item.id).delete();
        showToast("刪除成功", "success");
      },
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div style={{ marginBottom: 16 }}>
        <DataToolbar title="制式合約範本庫" canEdit={canManage} onCreate={handleCreate} onExport={() => {}} />

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            background: "#161616",
            padding: 12,
            borderRadius: 8,
            border: "1px solid #333",
          }}
        >
          <input
            className="input"
            style={{ width: 250 }}
            placeholder="搜尋範本名稱、編號..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="input"
            style={{ width: 200 }}
            value={contractTypeFilter}
            onChange={(e) => setContractTypeFilter(e.target.value)}
          >
            <option value="ALL">所有類型</option>
            {CONTRACT_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-8">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 20 }}>
          {filteredItems.map((item) => (
            <TemplateCard
              key={item.id}
              item={item}
              canManage={canManage}
              onView={() => handleView(item)}
              onFlow={() => handleFlow(item)}
              onEdit={() => handleEdit(item)}
              onDelete={() => handleDelete(item)}
            />
          ))}
          {filteredItems.length === 0 && (
            <div
              className="p"
              style={{
                gridColumn: "1/-1",
                textAlign: "center",
                marginTop: 40,
                background: "rgba(255,255,255,0.02)",
                padding: 40,
                borderRadius: 12,
              }}
            >
              無符合條件的範本
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <TemplateFormModal
          initialData={editingItem}
          onClose={() => {
            setIsModalOpen(false);
            setViewOnly(false);
          }}
          onSaved={handleSaved}
          existingItems={items}
          canManage={canManage && !viewOnly}
        />
      )}

      {isFlowModalOpen && flowItem && (
        <TemplateFlowModal
          template={flowItem}
          onClose={() => {
            setIsFlowModalOpen(false);
            setFlowItem(null);
          }}
        />
      )}
    </div>
  );
}

function TemplateCard({
  item,
  canManage,
  onView,
  onFlow,
  onEdit,
  onDelete,
}: {
  item: ContractTemplate;
  canManage: boolean;
  onView: () => void;
  onFlow: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const statusColor =
    item.status === "APPROVED" || item.status === "PUBLISHED"
      ? "var(--ok)"
      : item.status === "REJECTED"
      ? "var(--danger)"
      : item.status === "IN_REVIEW"
      ? "#fbbf24"
      : "#666";

  const displayStatus = (item.status === "PUBLISHED" ? "APPROVED" : item.status) || "DRAFT";

  return (
    <div
      className="tile"
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 280,
        borderLeft: `4px solid ${statusColor}`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <span className="badge" style={{ background: "#333", color: "#fff", border: "none" }}>
          {item.contractType}
        </span>
        <span className="badge" style={{ color: statusColor, borderColor: statusColor, fontSize: 11 }}>
          {displayStatus}
        </span>
      </div>

      <h3 style={{ fontSize: 16, fontWeight: "bold", color: "#fff", marginBottom: 4, lineHeight: 1.4 }}>
        {item.title}
      </h3>
      <div style={{ fontSize: 12, color: "var(--muted)", fontFamily: "monospace", marginBottom: 8 }}>{item.docCode}</div>

      <div style={{ fontSize: 12, color: "#a5b4fc", marginBottom: 8 }}>適用對象：{item.targetAudience || "通用"}</div>

      <div
        style={{
          fontSize: 13,
          color: "#ccc",
          flex: 1,
          marginBottom: 16,
          lineHeight: 1.5,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {item.description || "無說明"}
      </div>

      <div style={{ marginTop: "auto", paddingTop: 12, borderTop: "1px dashed #333" }}>
        <div style={{ marginBottom: 12 }}>
          {item.attachments && item.attachments.length > 0 ? (
            item.attachments.map((f: any, i: number) => (
              <a
                key={i}
                href={f.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  color: "#60a5fa",
                  textDecoration: "none",
                  marginBottom: 4,
                }}
              >
                📄 {f.name}
              </a>
            ))
          ) : (
            <span style={{ fontSize: 12, color: "#666" }}>無附件</span>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
          <button className="smallbtn" onClick={onView}>
            檢視
          </button>
          <button className="smallbtn" onClick={onFlow}>
            簽核流程
          </button>
          {canManage && (
            <>
              <button className="smallbtn" onClick={onEdit}>
                編輯
              </button>
              <button
                className="smallbtn"
                style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                onClick={onDelete}
              >
                刪除
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateFormModal({ initialData, existingItems, onClose, canManage }: any) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [form, setForm] = useState(
    initialData || {
      title: "",
      docCode: "",
      description: "",
      targetAudience: "",
      contractType: "",
      departments: ["all"],
      attachments: [],
      status: "DRAFT",
    }
  );
  const [isSaving, setIsSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [aiLoading, setAiLoading] = useState(false);


  useEffect(() => {
    if (!initialData && !form.docCode) {
      const code = generateDocCode("template", new Date(), existingItems);
      setForm((p: any) => ({ ...p, docCode: code }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAiGenerate = async () => {
    const targetFile = pendingFiles[0];
    const existingFile = form.attachments && form.attachments.length > 0 ? form.attachments[0] : null;

    if (!targetFile && !existingFile) {
      showToast("請先上傳範本檔案 (PDF/Word)，再啟動 AI 產生草稿。", "error");
      return;
    }

    setAiLoading(true);
    try {
      const result: any = await analyzeUploadedFile({
        file: targetFile || undefined,
        fileMeta: !targetFile && existingFile ? (existingFile as any) : undefined,
        mode: "analysis",
        documentType: "TEMPLATE_STD",
        userInstruction:
          "請協助解析此制式合約範本附件，輸出『務實法務版 + 管理報告版』（管理報告版放上方），並以進攻談判角度整理可談項與底線。請回傳可回填欄位：title(範本名稱)、counterparty(適用對象/相對人)、summary(摘要)。請以繁中輸出，並盡量回傳欄位 key。",
      });

      const f: any = result?.extractedFields || result?.fields || {};
      const title = String(f.title || "").trim();
      const counterparty = String(f.counterparty || f.targetAudience || "").trim();
      const summaryText = String(result?.summary || "").trim();

      setForm((prev: any) => ({
        ...prev,
        title: !prev.title && title ? title : prev.title,
        targetAudience: !prev.targetAudience && counterparty ? counterparty : prev.targetAudience,
        description: !prev.description && summaryText ? summaryText : prev.description,
      }));

      showToast("AI 解析完成，已填入建議內容");
    } catch (e) {
      console.error(e);
      showToast("AI 解析失敗，請確認檔案格式", "error");
    } finally {
      setAiLoading(false);
    }
  };

  const toggleDept = (code: string) => {
    const current = form.departments || [];
    if (code === "all") {
      if (current.includes("all")) {
        setForm({ ...form, departments: [] });
      } else {
        setForm({ ...form, departments: ["all"] });
      }
    } else {
      let next = [...current];
      if (next.includes("all")) next = [];
      if (next.includes(code)) next = next.filter((c: string) => c !== code);
      else next.push(code);
      setForm({ ...form, departments: next });
    }
  };

  const saveTemplate = async (targetStatus: string) => {
    if (!form.title) return alert("請填寫「範本名稱」");
    if (!form.contractType) return alert("請選擇「合約類型」");

    if (targetStatus === "IN_REVIEW") {
      if (!form.targetAudience) return alert("送出簽核前，請填寫「適用對象」");
      if (!form.description) return alert("送出簽核前，請填寫「說明/摘要」");
      if (form.departments.length === 0) return alert("請選擇至少一個「可見部門」");
      if ((!form.attachments || form.attachments.length === 0) && pendingFiles.length === 0) {
        return alert("請上傳至少一個範本檔案");
      }
      if (!confirm("確定要送出簽核嗎？簽核流程將啟動，且暫時無法修改內容。")) return;
    }

    setIsSaving(true);
    try {
      let finalAttachments = [...(form.attachments || [])];
      if (pendingFiles.length > 0) {
        const newFiles = await uploadUnifiedFiles(pendingFiles, "TEMPLATE", initialData?.id || "temp");
        finalAttachments = [...finalAttachments, ...newFiles];
      }

      const payload: any = {
        ...form,
        attachments: finalAttachments,
        status: targetStatus,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: user?.displayName,
      };

      let docId = initialData?.id;
      if (docId) {
        await db.collection("contract_templates").doc(docId).update(payload);
      } else {
        payload.createdAt = serverTimestamp();
        payload.createdBy = user?.displayName;
        const ref = await db.collection("contract_templates").add(payload);
        docId = ref.id;
      }

      if (targetStatus === "IN_REVIEW") {
        await createOrResetApprovalFlow("TEMPLATE_STD", docId, user?.id || "", "法務部", user?.displayName);
        showToast("範本已儲存並啟動簽核流程");
      } else {
        showToast("範本草稿已儲存");
      }

      onClose();
    } catch (e) {
      console.error(e);
      showToast("儲存失敗", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal title={initialData ? "編輯制式範本" : "新增制式範本"} onClose={onClose} width={950}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div className="grid">
            <div className="field">
              <label className="label">範本編號</label>
              <input
                className="input"
                value={form.docCode}
                readOnly
                style={{ background: "rgba(255,255,255,0.05)", color: "#fbbf24" }}
              />
            </div>
            <div className="field">
              <label className="label">
                合約類型 <span style={{ color: "red" }}>*</span>
              </label>
              <select
                className="input"
                value={form.contractType}
                onChange={(e) => setForm({ ...form, contractType: e.target.value })}
                disabled={!canManage}
              >
                <option value="">請選擇</option>
                {CONTRACT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field" style={{ gridColumn: "1/-1" }}>
              <AiFieldHeader
                label="範本名稱 (必填)"
                loading={aiLoading}
                onClick={handleAiGenerate}
                disabled={!canManage || (pendingFiles.length === 0 && (!form.attachments || form.attachments.length === 0))}
              />
              <input
                className="input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                disabled={!canManage}
                placeholder="例如：KOL 代言合作契約（標準版）"
              />
            </div>

            <div className="field" style={{ gridColumn: "1/-1" }}>
              <label className="label">適用對象</label>
              <input
                className="input"
                value={form.targetAudience}
                onChange={(e) => setForm({ ...form, targetAudience: e.target.value })}
                disabled={!canManage}
                placeholder="例如：行銷部、影音創作者接案..."
              />
            </div>

            <div className="field" style={{ gridColumn: "1/-1" }}>
              <label className="label">說明 / 摘要</label>
              <textarea
                className="input"
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                disabled={!canManage}
                placeholder="說明此範本的使用情境與注意事項..."
              />
            </div>

            <div className="field" style={{ gridColumn: "1/-1" }}>
              <label className="label">
                可見部門 (核准後生效) <span style={{ color: "red" }}>*</span>
              </label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  background: "rgba(255,255,255,0.03)",
                  padding: 12,
                  borderRadius: 8,
                }}
              >
                <label
                  className="badge"
                  style={{
                    background: form.departments.includes("all") ? "#3b82f6" : "#333",
                    cursor: canManage ? "pointer" : "default",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.departments.includes("all")}
                    onChange={() => toggleDept("all")}
                    style={{ display: "none" }}
                    disabled={!canManage}
                  />
                  全公司通用
                </label>
                {DEPARTMENTS.map((d) => (
                  <label
                    key={d.id}
                    className="badge"
                    style={{
                      background:
                        form.departments.includes(d.name) && !form.departments.includes("all") ? "#3b82f6" : "#333",
                      cursor: canManage ? "pointer" : "default",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.departments.includes(d.name)}
                      onChange={() => toggleDept(d.name)}
                      style={{ display: "none" }}
                      disabled={!canManage}
                    />
                    {d.name}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <FileUploadKit
              label="上傳範本檔案 (PDF/Word)"
              accept={[".pdf", ".doc", ".docx"]}
              existingFiles={form.attachments}
              rawFiles={pendingFiles}
              disabled={!canManage}
              onFilesSelected={(fs) => setPendingFiles((prev: File[]) => [...prev, ...fs])}
              onRemoveRawFile={(idx) => setPendingFiles((prev: File[]) => prev.filter((_, i) => i !== idx))}
              onRemoveFile={(f, i) => {
                const next = [...form.attachments];
                next.splice(i, 1);
                setForm({ ...form, attachments: next });
              }}
            />
          </div>

          {canManage && (
            <div className="row" style={{ marginTop: "auto", paddingTop: 20, justifyContent: "flex-end", gap: 12 }}>
              <button
                className="btn"
                style={{ width: "auto", background: "transparent", border: "1px solid #666", color: "#fbbf24" }}
                onClick={handleAiGenerate}
                disabled={
                  !canManage ||
                  aiLoading ||
                  (pendingFiles.length === 0 && (!form.attachments || form.attachments.length === 0))
                }
                type="button"
              >
                {aiLoading ? "AI 分析中..." : "AI 產生草稿"}
              </button>
              <button
                className="btn"
                style={{ width: "auto", background: "transparent", border: "1px solid #666", color: "#ccc" }}
                onClick={() => saveTemplate("DRAFT")}
                disabled={isSaving}
              >
                僅儲存草稿
              </button>
              <button
                className="btn"
                style={{ width: "auto", background: "#3b82f6", border: "none", color: "white" }}
                onClick={() => saveTemplate("IN_REVIEW")}
                disabled={isSaving}
              >
                {isSaving ? "處理中..." : "送出"}
              </button>
            </div>
          )}
        </div>
        </div>
    </Modal>
  );
}


function TemplateFlowModal({
  template,
  onClose,
}: {
  template: ContractTemplate;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const { flow, loading } = useApprovalFlow("TEMPLATE_STD", template.id);

  const handleDecision = async (
    stageKey: ApprovalStageKey,
    decision: ApprovalStageStatus,
    comment?: string,
    deptCode?: string,
    isDirectSign?: boolean
  ) => {
    if (!flow?.id) {
      alert("尚未建立簽核流程（可能尚未送出簽核）。");
      return;
    }

    try {
      await updateStageDecision({
        flowId: flow.id,
        stageKey,
        decision,
        comment: comment ?? "",
        actorUid: (user as any)?.uid,
        actorName: user?.displayName || user?.email || "User",
        actorEmail: user?.email || "",
        deptCode,
        isDirectSign,
      });

      // 同步主資料狀態（TEMPLATE_STD 不在 approvalFlowService 的自動回寫清單內）
      if (decision === "REJECTED" || decision === "RETURNED") {
        await db.collection("contract_templates").doc(template.id).update({
          status: "REJECTED",
          rejectReason: comment ?? "",
          updatedAt: new Date().toISOString(),
        });
      } else if (stageKey === "MANAGER_GM" && decision === "APPROVED") {
        await db.collection("contract_templates").doc(template.id).update({
          status: "APPROVED",
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (err: any) {
      console.error(err);
      alert(err?.message || "簽核更新失敗");
    }
  };

  return (
    <Modal
      isOpen
      title={`簽核流程｜${template.title || "未命名"}`}
      onClose={onClose}
      width={980}
    >
      {loading ? (
        <p style={{ margin: 0, opacity: 0.8 }}>載入中...</p>
      ) : flow ? (
        <ApprovalFlowPanel
          flow={flow}
          stageKey={(flow as any)?.activeStageKey}
          onClose={onClose}
          onDecision={handleDecision}
        />
      ) : (
        <div style={{ padding: 12, border: "1px dashed #444", borderRadius: 8, opacity: 0.85 }}>
          尚未建立簽核流程（此範本可能仍為草稿，尚未送出簽核）。
        </div>
      )}
    </Modal>
  );
}
